import { useEffect, useState } from "react";
import { getFirebaseDB, getFirebaseFunctions } from "@/lib/firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { toast } from "sonner";

// Helper to format Firestore timestamps
const formatDate = (date: any): string => {
  if (!date) return "N/A";
  if (typeof date === "string") return date;
  if (date.seconds) {
    return new Date(date.seconds * 1000).toLocaleDateString();
  }
  return "N/A";
};

interface Lease {
  id: string;
  propertyId: string;
  tenantId: string;
  startDate: any;
  endDate: any;
  status: string;
  rentAmount: number;
  createdAt: { seconds: number };
}

export default function LeasesPage() {
  const [leases, setLeases] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    expiring: 0,
    expired: 0,
  });

  useEffect(() => {
    const db = getFirebaseDB();
    if (!db) return;

    const q = query(collection(db, "leases"), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leasesData: Lease[] = [];
      let active = 0;
      let expiring = 0;
      let expired = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        leasesData.push({
          id: doc.id,
          propertyId: data.propertyId,
          tenantId: data.tenantId,
          startDate: data.startDate,
          endDate: data.endDate,
          status: data.status || "active",
          rentAmount: data.rentAmount,
          createdAt: data.createdAt,
        });

        if (data.status === "active") active++;
        if (data.status === "expiring-soon") expiring++;
        if (data.status === "expired") expired++;
      });

      setLeases(leasesData);
      setStats({
        total: leasesData.length,
        active,
        expiring,
        expired,
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleTerminateLease = async (leaseId: string) => {
    try {
      const functions = getFirebaseFunctions();
      if (!functions) {
        toast.error("Firebase not initialized");
        return;
      }

      const adminOperations = httpsCallable(functions, "adminOperations");
      await adminOperations({
        action: "terminateLease",
        data: {
          leaseId,
          reason: "Terminated via admin dashboard",
        },
      });
      toast.success("Lease terminated successfully");
    } catch (error) {
      toast.error("Failed to terminate lease");
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Leases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {stats.active}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expiring Soon</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {stats.expiring}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {stats.expired}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leases Table */}
      <Card>
        <CardHeader>
          <CardTitle>Leases List</CardTitle>
          <CardDescription>Manage all leases in the system</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Property ID</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rent Amount</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leases.map((lease) => (
                  <TableRow key={lease.id}>
                    <TableCell className="font-medium">
                      {lease.propertyId}
                    </TableCell>
                    <TableCell>{formatDate(lease.startDate)}</TableCell>
                    <TableCell>{formatDate(lease.endDate)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          lease.status === "active"
                            ? "default"
                            : lease.status === "expiring-soon"
                            ? "secondary"
                            : "destructive"
                        }
                      >
                        {lease.status}
                      </Badge>
                    </TableCell>
                    <TableCell>₦{lease.rentAmount.toLocaleString()}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleTerminateLease(lease.id)}
                      >
                        Terminate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
